/**
 * cartCardData — derivation helpers for the in-flight cart card.
 *
 * Centralizes the "what does the FSR see on a cart card" logic so the card
 * component stays presentational. Every function here is pure + tolerant of
 * malformed JSON; nothing throws.
 *
 * IMPORTANT: this module never surfaces raw GUIDs, officer codes, or branch
 * codes. Codes are resolved against ConfigBundle so the card shows
 * "Bill Wiggins" and "Downtown Rolla", not "WW" and "01".
 */

import type { CartDto } from '@retail-ops/shared-types';
import type { Product, Branch } from '@retail-ops/shared-types';

// ---------------------------------------------------------------------------
// Primary applicant
// ---------------------------------------------------------------------------

export interface CartApplicantSummary {
  /** Display label — "Jane A. Doe Jr." or "New application — no applicant yet". */
  label: string;
  /** 2-letter initials for the avatar (e.g. "JD"). Empty when no applicant. */
  initials: string;
  /** Total applicants on the cart (>= 1 when label is a real person). */
  count: number;
  /** True when no applicant has been added yet. */
  isEmpty: boolean;
}

interface RawPerson {
  firstName?: unknown;
  middleName?: unknown;
  lastName?: unknown;
  suffix?: unknown;
  fullName?: unknown;
  customerType?: unknown;
}

/** Parse `cart.peopleJson` tolerating both the bare-array and `{people:[]}` envelope shapes. */
function parsePeopleArray(peopleJson: string | undefined): RawPerson[] {
  if (!peopleJson) return [];
  try {
    const raw = JSON.parse(peopleJson) as unknown;
    if (Array.isArray(raw)) return raw as RawPerson[];
    if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
      return (raw as { people: RawPerson[] }).people;
    }
    return [];
  } catch {
    return [];
  }
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Build the applicant summary for the card identity strip. */
export function summarizePrimaryApplicant(cart: CartDto): CartApplicantSummary {
  const people = parsePeopleArray(cart.peopleJson);
  if (people.length === 0) {
    return { label: 'New application', initials: '', count: 0, isEmpty: true };
  }
  const first = people[0] ?? {};
  const fn = asString(first.firstName);
  const mn = asString(first.middleName);
  const ln = asString(first.lastName);
  const sf = asString(first.suffix);
  const fullName = asString(first.fullName);

  let label = '';
  if (fn || ln) {
    const parts = [fn, mn ? `${mn[0]}.` : '', ln, sf].filter(Boolean);
    label = parts.join(' ');
  } else if (fullName) {
    label = fullName;
  } else {
    label = 'Unnamed applicant';
  }

  const ini = computeInitials(fn, ln, fullName);
  return { label, initials: ini, count: people.length, isEmpty: false };
}

function computeInitials(firstName: string, lastName: string, fullName: string): string {
  if (firstName || lastName) {
    return ((firstName[0] ?? '') + (lastName[0] ?? '')).toUpperCase() || '??';
  }
  if (fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
  }
  return '??';
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export interface CartProductChip {
  code: string;
  name: string;
}

interface RawProduct {
  code?: unknown;
  qty?: unknown;
}

/** Resolve `cart.productsJson` → display-name chips via the product catalog. */
export function summarizeProductChips(
  cart: CartDto,
  catalog: ReadonlyArray<Product>,
): ReadonlyArray<CartProductChip> {
  if (!cart.productsJson) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(cart.productsJson);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  const byCode = new Map<string, Product>();
  for (const p of catalog) byCode.set(p.code, p);

  const out: CartProductChip[] = [];
  for (const item of raw as RawProduct[]) {
    const code = typeof item?.code === 'string' ? item.code : '';
    if (!code) continue;
    const def = byCode.get(code);
    out.push({ code, name: def?.name ?? code });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Officer + branch resolution
// ---------------------------------------------------------------------------

export interface OfficerLookup {
  /** Display name for the officer code, or undefined if unknown. */
  resolve: (officerCode: string | undefined) => string | undefined;
}

/**
 * Build an officer-code → display-name resolver from anything ConfigBundle
 * exposes. Today the bundle doesn't carry a canonical officer roster on the
 * client; we fall back to a humanized version of the officer code so the
 * card NEVER displays a raw 2-letter code like "WW".
 */
export function humanizeOfficer(officerCode: string | undefined, currentUserDisplayName?: string, currentUserOfficerCode?: string): string {
  const code = (officerCode ?? '').trim();
  if (!code) return 'Unassigned';
  if (currentUserOfficerCode && currentUserDisplayName && code.toUpperCase() === currentUserOfficerCode.toUpperCase()) {
    return currentUserDisplayName;
  }
  // Fallback: render a bracketed label so the user knows it's a code, not a name.
  return `Officer ${code.toUpperCase()}`;
}

/** Resolve a branch code to the display name (`branchName`) from the catalog. */
export function humanizeBranch(
  branchCode: string | undefined,
  catalog: ReadonlyArray<Branch>,
): string {
  const code = (branchCode ?? '').trim();
  if (!code) return 'Unassigned branch';
  const found = catalog.find((b) => b.branchCode.toUpperCase() === code.toUpperCase());
  return found?.branchName ?? `Branch ${code}`;
}

// ---------------------------------------------------------------------------
// Stage / pipeline-step humanization
// ---------------------------------------------------------------------------

const STAGE_ICONS: Record<string, string> = {
  people: 'People',
  products: 'Products',
  ownership: 'Ownership',
  cip: 'CIP review',
  compliance: 'Compliance',
  review: 'Review',
  signing: 'Signing',
  signatures: 'Signing',
  commit: 'Commit',
  committed: 'Committed',
};

export interface StageSummary {
  /** Human-readable stage label (Title-Cased). */
  label: string;
  /** Single emoji glyph that conveys the stage at a glance. */
  glyph: string;
}

const STAGE_GLYPHS: Record<string, string> = {
  people: 'P',
  products: 'PR',
  ownership: 'O',
  cip: 'C',
  compliance: 'CO',
  review: 'R',
  signing: 'S',
  signatures: 'S',
  commit: 'X',
  committed: 'X',
};

/** Humanize the pipelineStep code (e.g. "cip" → { label: "CIP review", glyph: "C" }). */
export function summarizeStage(pipelineStep: string | undefined): StageSummary {
  const key = (pipelineStep ?? '').trim().toLowerCase();
  if (!key) return { label: 'Not started', glyph: '*' };
  return {
    label: STAGE_ICONS[key] ?? toTitleCase(key),
    glyph: STAGE_GLYPHS[key] ?? '*',
  };
}

function toTitleCase(s: string): string {
  return s
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((p) => (p[0] ?? '').toUpperCase() + p.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Status pill mapping
// ---------------------------------------------------------------------------

export type CartPillVariant = 'ok' | 'warn' | 'err' | 'info' | 'muted';

export interface StatusSummary {
  variant: CartPillVariant;
  label: string;
}

/** Map CartStatus → human label + Pill variant. */
export function summarizeStatus(status: string | undefined): StatusSummary {
  switch ((status ?? '').toLowerCase()) {
    case 'draft':
      return { variant: 'muted', label: 'Draft' };
    case 'active':
      return { variant: 'info', label: 'In progress' };
    case 'awaiting-signatures':
      return { variant: 'warn', label: 'Awaiting signatures' };
    case 'pipeline-error':
      return { variant: 'err', label: 'Needs attention' };
    case 'committing':
      return { variant: 'info', label: 'Committing' };
    case 'committed':
      return { variant: 'ok', label: 'Committed' };
    case 'archived':
      return { variant: 'muted', label: 'Archived' };
    default:
      return { variant: 'muted', label: status ?? 'Unknown' };
  }
}

// ---------------------------------------------------------------------------
// Relative time formatting
// ---------------------------------------------------------------------------

/**
 * "2 min ago", "3 hr ago", "Yesterday", absolute date for older.
 *
 * Distinct from `utils/formatting.formatRelative` which uses compact tokens
 * (`5m`, `3h`); this version reads more naturally on a user-facing card.
 */
export function formatRelativeFriendly(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'Just now';
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin === 1) return '1 min ago';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH === 1) return '1 hr ago';
  if (diffH < 24) return `${diffH} hr ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'Yesterday';
  if (diffD < 7) return `${diffD} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── BACKLOG-1666 (Bill 2026-10-01: "Cart listing needs to provide more identifying
// information: Owners, Beneficiaries, Products, etc.") ───────────────────────────

/** Every owner name on the cart, in people order. */
export function summarizeOwnerNames(cart: CartDto): string[] {
  return parsePeopleArray(cart.peopleJson)
    .map((p) => {
      const fn = asString(p.firstName);
      const ln = asString(p.lastName);
      const full = asString(p.fullName);
      return (fn || ln) ? [fn, ln].filter(Boolean).join(' ') : full;
    })
    .filter((n) => n.length > 0);
}

/** Total POD / beneficiary entries across every product line (cart.metadataJson.beneficiaries). */
export function summarizeBeneficiaryTotal(cart: CartDto): number {
  if (!cart.metadataJson) return 0;
  try {
    const meta = JSON.parse(cart.metadataJson) as Record<string, unknown>;
    const map = meta['beneficiaries'];
    if (!map || typeof map !== 'object' || Array.isArray(map)) return 0;
    return Object.values(map as Record<string, unknown>)
      .reduce<number>((n, rows) => n + (Array.isArray(rows) ? rows.length : 0), 0);
  } catch {
    return 0;
  }
}
